/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import {
  Directive,
  ElementRef,
  OnDestroy,
  ChangeDetectorRef,
  Renderer2,
  NgZone,
  Input,
  ViewContainerRef,
  OnInit,
  ComponentFactoryResolver,
  ComponentRef,
  Type
} from '@angular/core';

import { ChangedEvent, TextService } from '@typescript-kit/core';
import { ValueModel, TextModel, ComponentModel } from '@typescript-kit/view';

import { BaseDirective } from './base.directive';
import { TextComponent } from '../component/text/text.component';
import { ComponentProvider } from '../provider/component.provider';

@Directive({
  selector: '[kitValue]'
})
export class ValueDirective extends BaseDirective<ValueModel> implements OnInit, OnDestroy {
  private alignmentClass: string;
  private suffixComponentRef: ComponentRef<BaseDirective<ComponentModel>>;

  constructor(
    private viewContainerRef: ViewContainerRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    changeDetector: ChangeDetectorRef,
    elementRef: ElementRef,
    renderer: Renderer2,
    ngZone: NgZone,
    textService: TextService
  ) {
    super(changeDetector, elementRef, renderer, ngZone, textService);
  }

  get hostClass(): string {
    return 'kit-value';
  }

  @Input()
  set kitValue(model: ValueModel) {
    this.model = model;
  }

  get kitValue(): ValueModel {
    return this.model;
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  protected onModelChanged(model: ValueModel) {
    super.onModelChanged(model);
    this.refreshValue();
    this.refreshSuffix();
    this.refreshAlignmentClass();
    this.refreshErrorClass();
  }

  protected onModelPropertyChanged(event: ChangedEvent): void {
    super.onModelPropertyChanged(event);
    if (event.changes['formattedValue']) {
      this.refreshValue();
    }
    if (event.changes['suffix']) {
      this.refreshSuffix();
    }
    if (event.changes['alignment']) {
      this.refreshAlignmentClass();
    }
    if (event.changes['isValid']) {
      this.refreshErrorClass();
    }
  }

  protected refreshValue() {
    // if (!this.valueComponent) {
    //   this.valueComponentRef = this.createTextComponent();
    // }
    // this.valueComponent.model.text = this.model.formattedValue;
    this.element.innerText = this.textService.getText(this.model.formattedValue);
  }

  protected refreshSuffix() {
    if (this.suffixComponentRef) {
      if (this.model.isHidden || this.model.suffix === undefined || this.model.suffix === null) {
        this.viewContainerRef.remove(0);
        this.suffixComponentRef = null;
      } else if (typeof this.model.suffix !== 'string') {
        this.viewContainerRef.remove(0);
        this.suffixComponentRef = this.createComponent(this.model.suffix);
      } else if (this.suffixComponentRef.instance instanceof TextComponent) {
        this.suffixComponentRef.instance.model.text = this.model.suffix;
      } else {
        this.viewContainerRef.remove(0);
        this.suffixComponentRef = this.createComponent(new TextModel({
          tags: ['kit-value-suffix'],
          text: this.model.suffix
        }));
      }
    } else {
      if (this.model.isHidden || this.model.suffix === undefined || this.model.suffix === null) {
        return;
      }
      if (typeof this.model.suffix === 'string') {
        this.suffixComponentRef = this.createComponent(new TextModel({
          tags: ['kit-value-suffix'],
          text: this.model.suffix
        }));
      } else if (this.model.suffix) {
        this.suffixComponentRef = this.createComponent(this.model.suffix);
      }
    }
  }

  private createComponent(model: ComponentModel): ComponentRef<BaseDirective<ComponentModel>> {
    const componentType: Type<any> = ComponentProvider.resolveComponentType(model.type);
    if (!componentType) {
      // TODO
      return;
    }
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentType);
    const componentRef = this.viewContainerRef.createComponent(componentFactory);
    componentRef.instance.model = model;
    return componentRef;
  }

  private createTextComponent(): ComponentRef<TextComponent> {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(TextComponent);
    const textComponentRef = this.viewContainerRef.createComponent(componentFactory);
    textComponentRef.instance.model = new TextModel({ text: '' });
    return textComponentRef;
  }

  private refreshAlignmentClass() {
    if (this.alignmentClass) {
      this.renderer.removeClass(this.element, this.alignmentClass);
      this.alignmentClass = null;
    }
    if (this.model && this.model.alignment) {
      this.alignmentClass = `kit-alignment-${this.model.alignment}`;
      this.renderer.addClass(this.element, this.alignmentClass);
    }
  }

  private refreshErrorClass() {
    if (this.model.isValid === false) {
      this.renderer.addClass(this.element, 'app-has-error');
    } else {
      this.renderer.removeClass(this.element, 'app-has-error');
    }
  }

}
